import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { getSession } from "../functions";

/**
 * FinancialYearReportPDF
 * -----------------------------------------------------------------------
 * Generates a professional, financial-year-scoped report (Summary or
 * Detailed) covering:
 *   - Fee collection (Cash / UPI / NEFT / Card Swap / Net Banking / Online)
 *   - Admission Form Fee collection
 *   - Miscellaneous Fee collection
 *   - Outstanding Dues (as of report generation date)
 *
 * Page 1 is a photo cover page (campus aerial shot). Page 2 opens with a
 * slim single-line header, then a dashboard: icon-style KPI cards, a
 * payment-mode donut chart + a "Top Fee Heads" leader-line list side by
 * side, and a month-by-month collection trend line chart — all charts are
 * drawn on an off-screen <canvas> and embedded as images, since jsPDF has
 * no native charting.
 *
 * Usage:
 *   FinancialYearReportPDF({
 *     feeData,                 // same shape as DcrSchoolPDF's feeData param
 *     admissionFormData,       // admission form entries filtered to the FY
 *     miscellaneousFeesData,   // misc fee entries filtered to the FY
 *     duesFeeData,             // raw duesFeeSlice data (unfiltered)
 *     financialYearLabel,      // e.g. "2025-26"
 *     reportType,              // "summary" | "detailed"
 *   });
 * -----------------------------------------------------------------------
 */

// ── Design tokens ──────────────────────────────────────────────────────────
// `navy` / `gold` keep their original values — they're still used by the
// section-heading accent bar and every autoTable header further down, none
// of which changed in this pass.
const COLORS = {
  primary: [239, 78, 36], // #ef4e24
  primaryDark: [176, 48, 18],
  primaryLight: [253, 226, 214],
  ink: [32, 30, 34],
  navy: [21, 41, 71],
  navyDark: [13, 27, 48],
  gold: [190, 149, 73],
  slate: [95, 104, 118],
  white: [255, 255, 255],
  cream: [250, 247, 242],
  bgPanel: [244, 246, 250],
  border: [230, 231, 235],
  green: [33, 140, 88],
  greenBg: [224, 245, 234],
  red: [196, 58, 58],
  redDark: [138, 30, 30],
  redBg: [253, 230, 230],
  blue: [42, 104, 196],
  blueBg: [224, 236, 253],
  amber: [188, 132, 20],
  amberBg: [255, 243, 221],
  purple: [107, 78, 176],
  purpleBg: [235, 229, 249],
};

// Payment-mode → colour, reused by the KPI legend and the donut chart so
// the same mode always reads as the same colour across the report.
const MODE_COLORS = {
  Cash: COLORS.amber,
  UPI: COLORS.blue,
  NEFT: COLORS.purple,
  "Card Swap": COLORS.green,
  "Net Banking": COLORS.red,
  Online: COLORS.primary,
};
const modeColor = (mode) => MODE_COLORS[mode] || COLORS.slate;

const fmt = (n) => (Number(n) || 0).toLocaleString("en-IN");
const rgbStr = (arr) => `rgb(${arr[0]},${arr[1]},${arr[2]})`;
const rgbaStr = (arr, a) => `rgba(${arr[0]},${arr[1]},${arr[2]},${a})`;

// Compact Indian-style abbreviation (₹2.65Cr / 66.3K) for the chart's
// y-axis only — the full comma-grouped figure (e.g. "2,65,03,972") is too
// long to fit against the plot without crowding or clipping, however much
// left padding is given. Everywhere else in the report keeps the full
// number via fmt().
const compactINR = (n) => {
  const num = Number(n) || 0;
  const abs = Math.abs(num);
  const trim = (s) => s.replace(/\.0+$/, "").replace(/(\.\d*[1-9])0+$/, "$1");
  if (abs >= 1e7) return `${trim((num / 1e7).toFixed(2))}Cr`;
  if (abs >= 1e5) return `${trim((num / 1e5).toFixed(2))}L`;
  if (abs >= 1e3) return `${trim((num / 1e3).toFixed(1))}K`;
  return num.toLocaleString("en-IN");
};

// ── Layout helpers ──────────────────────────────────────────────────────────
const ensureSpace = (doc, y, needed, marginBottom = 18) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y + needed > pageHeight - marginBottom) {
    doc.addPage();
    return 20;
  }
  return y;
};

const sectionHeading = (doc, text, y) => {
  y = ensureSpace(doc, y, 14);
  doc.setFillColor(...COLORS.gold);
  doc.rect(14, y, 3, 6, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11.5);
  doc.setTextColor(...COLORS.navy);
  doc.text(text, 20, y + 5);
  doc.setTextColor(0, 0, 0);
  return y + 12;
};

// ── KPI cards (icon-badge dashboard style) ──────────────────────────────
// Replaces the old accent-bar stat cards entirely. Each card gets a small
// coloured "icon" badge + a small-caps label on the top row, and a large
// bold value beneath — the same rhythm as a typical analytics-dashboard
// stat card. `featured: true` cards (Total Collection, Total Outstanding
// Dues) render as solid colour blocks so the headline numbers stand out.
const drawKpiCard = (doc, x, y, w, h, { label, value, color, featured }) => {
  const r = 2.6;

  if (featured) {
    doc.setFillColor(...color);
    doc.roundedRect(x, y, w, h, r, r, "F");

    doc.setFillColor(...COLORS.white);
    doc.circle(x + 10, y + 10, 4.2, "F");
    doc.setFillColor(...color);
    doc.circle(x + 10, y + 10, 1.9, "F");

    doc.setTextColor(...COLORS.white);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.6);
    doc.text(label.toUpperCase(), x + 17, y + 11.5, { maxWidth: w - 24 });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(15.5);
    doc.text(value, x + 9, y + h - 7);
  } else {
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(x, y, w, h, r, r, "F");
    doc.setDrawColor(...COLORS.border);
    doc.setLineWidth(0.25);
    doc.roundedRect(x, y, w, h, r, r, "S");

    doc.setFillColor(...color);
    doc.circle(x + 10, y + 10, 4.2, "F");
    doc.setFillColor(...COLORS.white);
    doc.circle(x + 10, y + 10, 1.7, "F");

    doc.setTextColor(...COLORS.slate);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.3);
    doc.text(label.toUpperCase(), x + 17, y + 11.5, { maxWidth: w - 24 });

    doc.setTextColor(...COLORS.ink);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(value, x + 9, y + h - 7);
  }
  doc.setTextColor(0, 0, 0);
};

const drawKpiGrid = (doc, cards, startY, opts = {}) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = opts.margin ?? 14;
  const gap = opts.gap ?? 5;
  const perRow = opts.perRow ?? 4;
  const cardH = opts.cardH ?? 27;
  const usableWidth = pageWidth - margin * 2;
  const cardW = (usableWidth - gap * (perRow - 1)) / perRow;

  let x = margin;
  let y = ensureSpace(doc, startY, cardH + gap);
  cards.forEach((card, i) => {
    if (i > 0 && i % perRow === 0) {
      x = margin;
      y = ensureSpace(doc, y + cardH + gap, cardH + gap);
    }
    drawKpiCard(doc, x, y, cardW, cardH, card);
    x += cardW + gap;
  });
  return y + cardH + gap;
};

// ── Charts (canvas-rendered, embedded as images) ─────────────────────────
// jsPDF has no native charting, so these build a small off-screen <canvas>,
// draw the chart with plain 2D canvas calls, and hand back a PNG data URL
// that gets embedded with doc.addImage(). Rendered at a higher pixel size
// than the mm footprint they're placed at, so they stay crisp when printed.
const buildDonutChartImage = (segments, size = 440) => {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  const total = segments.reduce((s, seg) => s + seg.value, 0) || 1;
  const cx = size / 2;
  const cy = size / 2;
  const outerR = size / 2 - 8;
  const innerR = outerR * 0.56;

  let startAngle = -Math.PI / 2;
  segments.forEach((seg) => {
    const angle = (seg.value / total) * Math.PI * 2;
    if (angle <= 0) return;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, outerR, startAngle, startAngle + angle);
    ctx.closePath();
    ctx.fillStyle = seg.colorStr;
    ctx.fill();
    startAngle += angle;
  });

  ctx.globalCompositeOperation = "destination-out";
  ctx.beginPath();
  ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalCompositeOperation = "source-over";

  return canvas.toDataURL("image/png");
};

const buildLineChartImage = (labels, values, opts = {}) => {
  const width = opts.width ?? 900;
  const height = opts.height ?? 320;
  const colorStr = opts.color ?? "rgb(239,78,36)";
  const fillStr = opts.fill ?? "rgba(239,78,36,0.12)";

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");

  const padL = 66;
  const padR = 18;
  const padT = 14;
  const padB = 30;
  const chartW = width - padL - padR;
  const chartH = height - padT - padB;
  const maxVal = Math.max(...values, 1);

  ctx.strokeStyle = "#e5e7eb";
  ctx.lineWidth = 1;
  for (let i = 0; i <= 4; i++) {
    const gy = padT + (chartH / 4) * i;
    ctx.beginPath();
    ctx.moveTo(padL, gy);
    ctx.lineTo(width - padR, gy);
    ctx.stroke();
  }

  ctx.fillStyle = "#6b7280";
  ctx.font = "15px Arial";
  ctx.textAlign = "right";
  ctx.textBaseline = "middle";
  for (let i = 0; i <= 4; i++) {
    const val = maxVal - (maxVal / 4) * i;
    const gy = padT + (chartH / 4) * i;
    ctx.fillText(compactINR(val), padL - 10, gy);
  }

  const stepX = chartW / Math.max(values.length - 1, 1);
  const pointX = (i) => padL + stepX * i;
  const pointY = (v) => padT + chartH - (v / maxVal) * chartH;

  // Area fill under the line
  ctx.beginPath();
  values.forEach((v, i) => {
    const x = pointX(i);
    const y = pointY(v);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.lineTo(pointX(values.length - 1), padT + chartH);
  ctx.lineTo(pointX(0), padT + chartH);
  ctx.closePath();
  ctx.fillStyle = fillStr;
  ctx.fill();

  // Line
  ctx.beginPath();
  values.forEach((v, i) => {
    const x = pointX(i);
    const y = pointY(v);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.strokeStyle = colorStr;
  ctx.lineWidth = 3;
  ctx.lineJoin = "round";
  ctx.stroke();

  // Markers
  values.forEach((v, i) => {
    const x = pointX(i);
    const y = pointY(v);
    ctx.beginPath();
    ctx.arc(x, y, 4.5, 0, Math.PI * 2);
    ctx.fillStyle = "#ffffff";
    ctx.fill();
    ctx.lineWidth = 2.2;
    ctx.strokeStyle = colorStr;
    ctx.stroke();
  });

  // X labels
  ctx.fillStyle = "#6b7280";
  ctx.font = "15px Arial";
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  labels.forEach((lab, i) => {
    ctx.fillText(lab, pointX(i), padT + chartH + 8);
  });

  return canvas.toDataURL("image/png");
};

// Donut chart (left) + a colour-coded legend with amount/percentage (right),
// drawn with plain jsPDF text/shapes since a legend is just labels.
const drawDonutWithLegend = (doc, x, y, diameterMM, segments) => {
  const total = segments.reduce((s, seg) => s + seg.value, 0) || 1;
  if (segments.length === 0 || total === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8.5);
    doc.setTextColor(...COLORS.slate);
    doc.text("No payment data for this period.", x, y + diameterMM / 2);
    doc.setTextColor(0, 0, 0);
    return y + diameterMM;
  }

  try {
    const dataUrl = buildDonutChartImage(
      segments.map((seg) => ({
        value: seg.value,
        colorStr: rgbStr(seg.color),
      })),
    );
    doc.addImage(dataUrl, "PNG", x, y, diameterMM, diameterMM);
  } catch (e) {
    // Canvas unavailable — fall back to legend-only, still useful.
  }

  const legendX = x + diameterMM + 10;
  let legendY = y + 4;
  segments.forEach((seg) => {
    const pct = ((seg.value / total) * 100).toFixed(1);
    doc.setFillColor(...seg.color);
    doc.roundedRect(legendX, legendY - 3.2, 4, 4, 1, 1, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.4);
    doc.setTextColor(...COLORS.ink);
    doc.text(seg.label, legendX + 7, legendY);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.6);
    doc.setTextColor(...COLORS.slate);
    doc.text(`${fmt(seg.value)}  \u2022  ${pct}%`, legendX + 7, legendY + 4.2);

    legendY += 10.5;
  });
  doc.setTextColor(0, 0, 0);
  return Math.max(y + diameterMM, legendY);
};

// "Top revenue drivers"-style leader-line list: label — dotted leader —
// right-aligned amount.
const drawLeaderList = (doc, x, y, width, items) => {
  let cy = y;
  items.forEach((item) => {
    const valueText = fmt(item.value);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.ink);
    const labelW = doc.getTextWidth(item.label);
    doc.text(item.label, x, cy);

    doc.setFont("helvetica", "bold");
    const valueW = doc.getTextWidth(valueText);
    doc.text(valueText, x + width, cy, { align: "right" });

    const dotsStart = x + labelW + 3;
    const dotsEnd = x + width - valueW - 3;
    if (dotsEnd > dotsStart) {
      doc.setLineDashPattern([0.6, 1.4], 0);
      doc.setDrawColor(...COLORS.border);
      doc.setLineWidth(0.4);
      doc.line(dotsStart, cy - 1, dotsEnd, cy - 1);
      doc.setLineDashPattern([], 0);
    }
    cy += 8.2;
  });
  doc.setTextColor(0, 0, 0);
  return cy;
};

// ── Cover page (page 1) ─────────────────────────────────────────────────
// Full-bleed campus photo across the whole page, with a light translucent
// wash over it so it reads as a soft background rather than a competing
// hero image — all title/meta content sits directly on top of it (via
// jsPDF's GState opacity API; falls back to a plain cream page if that or
// the photo itself isn't available).
const drawCoverPage = (
  doc,
  { financialYearLabel, reportType, session, fromDate, toDate },
) => {
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();

  // Base fill first, so there's always a sane background even if the photo
  // or the opacity API below isn't available.
  doc.setFillColor(...COLORS.cream);
  doc.rect(0, 0, pw, ph, "F");

  try {
    doc.addImage("/assets/images/building-bg.png", "PNG", 0, 0, pw, ph);
    // Lighten the whole photo with a translucent cream wash so it reads as
    // a background texture, not a competing full-strength image.
    doc.setGState(new doc.GState({ opacity: 0.86 }));
    doc.setFillColor(...COLORS.cream);
    doc.rect(0, 0, pw, ph, "F");
    doc.setGState(new doc.GState({ opacity: 1 }));
  } catch (e) {
    // photo/opacity unavailable — the plain cream fill above still stands.
  }

  // Thin brand-colour rule along the very top, so there's still one crisp
  // brand touch even though the photo itself is now just a soft backdrop.
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pw, 2.2, "F");

  // ── Content, placed directly on the light photo backdrop ────────────
  // Same 14mm left margin used everywhere else in the report (running
  // header, section headings, KPI cards, tables) so the left edge lines
  // up exactly from the cover through every following page.
  const marginX = 14;

  // Logo (no background box) + wordmark
  try {
    doc.addImage("/assets/images/icon.png", "PNG", marginX, 16, 14, 14);
  } catch (e) {
    // logo optional
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.ink);
  doc.text("Acharyakulam", marginX + 18, 24.5);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.primary);
  doc.text(`FY ${financialYearLabel}`, pw - marginX, 24.5, { align: "right" });

  // Title — "Report" sits at a true ~1.3 line-height below "Financial"
  // (40pt \u00d7 0.3528mm/pt \u00d7 1.3 \u2248 18.3mm) instead of the looser gap before.
  const titleSize = 40;
  const titleLineGap = titleSize * 0.3528 * 1.3;
  const titleY = 80;

  doc.setTextColor(...COLORS.ink);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(titleSize);
  doc.text("Financial", marginX, titleY);

  doc.setTextColor(...COLORS.primary);
  doc.text("Report", marginX, titleY + 10);

  const afterTitleY = titleY + titleLineGap + 15;

  doc.setTextColor(...COLORS.slate);
  doc.setFont("helvetica", "italic");
  doc.setFontSize(10.5);
  doc.text(
    "Precision, transparency and a complete year-round financial picture.",
    marginX,
    afterTitleY,
  );

  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(0.8);
  doc.line(marginX, afterTitleY + 6, marginX + 65, afterTitleY + 6);

  // Meta row: Prepared by / Financial Year / Report Type
  const metaTop = afterTitleY + 17;
  const metaItems = [
    { label: "PREPARED BY", value: "In-Build Reports" },
    { label: "FINANCIAL YEAR", value: financialYearLabel },
    {
      label: "REPORT TYPE",
      value: reportType === "summary" ? "Summary" : "Detailed",
    },
  ];
  metaItems.forEach((item, i) => {
    const x = marginX + i * 55;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.4);
    doc.setTextColor(...COLORS.slate);
    doc.text(item.label, x, metaTop);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.primary);
    doc.text(item.value, x, metaTop + 6.5, { maxWidth: 50 });
  });

  // One-line description
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.6);
  doc.setTextColor(...COLORS.slate);
  doc.text(
    "Compiled directly from verified payment, admission form fee, miscellaneous fee and live dues records.",
    marginX,
    metaTop + 18,
    { maxWidth: 170 },
  );

  // Small campus credit line, bottom-right — unobtrusive now that the
  // photo is a background wash rather than a captioned hero shot.
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.4);
  doc.setTextColor(...COLORS.slate);
  doc.text("Acharyakulam Campus", pw - marginX, ph - 12, { align: "right" });

  // Footer stamp
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.6);
  doc.setTextColor(...COLORS.slate);
  doc.text(
    `Period: ${new Date(fromDate).toLocaleDateString("en-GB")} \u2013 ${new Date(
      toDate,
    ).toLocaleDateString(
      "en-GB",
    )}   |   Session ${session}   |   Generated ${new Date().toLocaleString(
      "en-GB",
    )}`,
    marginX,
    ph - 12,
  );

  doc.setTextColor(0, 0, 0);
};

// ── Slim running header (page 2+) ────────────────────────────────────────
// The cover page already carries the full branding, so content pages get
// just a single-line strip for context (useful if pages get printed and
// separated) instead of repeating the heavy header band.
const drawRunningHeader = (doc, { financialYearLabel, reportType }) => {
  const pw = doc.internal.pageSize.getWidth();

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(...COLORS.primary);
  doc.text("ACHARYAKULAM  \u2022  FINANCIAL REPORT", 14, 14);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.slate);
  doc.text(
    `FY ${financialYearLabel}  \u2022  ${reportType === "summary" ? "Summary" : "Detailed"} Report`,
    pw - 14,
    14,
    { align: "right" },
  );

  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.3);
  doc.line(14, 18, pw - 14, 18);

  doc.setTextColor(0, 0, 0);
  return 26;
};

const addFooters = (doc, financialYearLabel) => {
  const totalPages = doc.internal.getNumberOfPages();
  // Start at page 2 — the cover page (page 1) intentionally carries no
  // page-number/footer strip, matching a standalone title page.
  for (let i = 2; i <= totalPages; i++) {
    doc.setPage(i);
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();
    doc.setDrawColor(...COLORS.bgPanel);
    doc.setLineWidth(0.3);
    doc.line(14, ph - 12, pw - 14, ph - 12);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.3);
    doc.setTextColor(140, 140, 140);
    doc.text(
      `Acharyakulam — Financial Report ${financialYearLabel}`,
      14,
      ph - 7.5,
    );
    doc.text(`Page ${i - 1} of ${totalPages - 1}`, pw - 14, ph - 7.5, {
      align: "right",
    });
    doc.setTextColor(0, 0, 0);
  }
};

// ── Data aggregation ──────────────────────────────────────────────────────
const aggregateFeeCollection = (allFeesData) => {
  const modeWiseData = {};
  const modeWiseTotals = {};
  const modeHeadWiseTotals = {};
  const totals = {
    admission: 0,
    development: 0,
    monthly: 0,
    transport: 0,
    lab: 0,
    grand: 0,
  };

  allFeesData.forEach((student) => {
    const {
      admission_no,
      receipt_no,
      date,
      student_name,
      class: studentClass,
      currentSection,
      collectedBy,
      feeDetails,
    } = student;

    if (!feeDetails) return;

    let studentTotal = 0;
    const paymentModes = {};

    const addPayment = (fee, type) => {
      if (fee?.paid) {
        studentTotal += fee.TotalAmt || 0;
        totals[type] += fee.TotalAmt || 0;
        Object.entries(fee.paymentMode || {}).forEach(([mode, { amount }]) => {
          paymentModes[mode] = (paymentModes[mode] || 0) + (amount || 0);
          if (!modeHeadWiseTotals[mode]) {
            modeHeadWiseTotals[mode] = {
              admission: 0,
              development: 0,
              monthly: 0,
              transport: 0,
            };
          }
          modeHeadWiseTotals[mode][type] += amount || 0;
        });
      }
    };

    addPayment(feeDetails.developmentCharges, "development");
    addPayment(feeDetails.admissionFee, "admission");

    const monthsPaidList = (feeDetails.monthPaid || [])
      .filter((m) => m.paid)
      .map((m) => {
        totals.monthly += m.monthlyFee || 0;
        totals.transport += m.transportFee || 0;
        totals.lab += m.labFee || 0;
        studentTotal += m.TotalAmt || 0;
        Object.entries(m.paymentMode || {}).forEach(([mode, { amount }]) => {
          paymentModes[mode] = (paymentModes[mode] || 0) + (amount || 0);
          if (!modeHeadWiseTotals[mode]) {
            modeHeadWiseTotals[mode] = {
              admission: 0,
              development: 0,
              monthly: 0,
              transport: 0,
            };
          }
          modeHeadWiseTotals[mode].monthly += m.monthlyFee || 0;
          modeHeadWiseTotals[mode].transport += m.transportFee || 0;
        });
        return m.month;
      })
      .join(", ");

    totals.grand += studentTotal;

    Object.entries(paymentModes).forEach(([mode, amount]) => {
      if (!modeWiseData[mode]) modeWiseData[mode] = [];
      modeWiseData[mode].push([
        modeWiseData[mode].length + 1,
        receipt_no,
        admission_no,
        new Date(date).toLocaleDateString("en-GB"),
        student_name,
        `${studentClass}, ${currentSection}`,
        monthsPaidList,
        studentTotal.toLocaleString("en-IN"),
        collectedBy || "N/A",
      ]);
      modeWiseTotals[mode] = (modeWiseTotals[mode] || 0) + amount;
    });
  });

  return { modeWiseData, modeWiseTotals, modeHeadWiseTotals, totals };
};

const aggregateSimpleFee = (data) => {
  let total = 0;
  const modeWise = {};
  (data || []).forEach((item) => {
    const amount = Number(item.amount) || 0;
    total += amount;
    const mode = item.mode || "N/A";
    modeWise[mode] = (modeWise[mode] || 0) + amount;
  });
  return { modeWise, total };
};

// Mirrors the "All months" dues calculation used on the Dues Fee page.
const aggregateDues = (duesFeeData) => {
  const dues = (duesFeeData || []).filter((s) => Number(s.monthlyFee || 0) > 0);
  let monthlyTotal = 0;
  let transportTotal = 0;
  let siblingTotal = 0;
  let studentCount = 0;

  dues.forEach((student) => {
    const unpaidMonths = student.unpaidMonths || [];
    if (unpaidMonths.length === 0) return;
    studentCount += 1;

    const monthlyFee = student.monthlyFee || 0;
    const transportFee = student.transportFee || 0;
    const transportableMonths = unpaidMonths.filter((m) => m !== "June");
    const studentTotalDue =
      monthlyFee * unpaidMonths.length +
      transportFee * transportableMonths.length;

    monthlyTotal += monthlyFee * unpaidMonths.length;
    transportTotal += transportFee * transportableMonths.length;
    if (student.scholarshipType === "sibling") siblingTotal += studentTotalDue;
  });

  return {
    studentCount,
    totalMonthlyDue: monthlyTotal,
    totalTransportDue: transportTotal,
    totalDueAllStudents: monthlyTotal + transportTotal,
    totalDueOfSiblingStudents: siblingTotal,
    dues,
  };
};

// Buckets total collection by calendar month, reordered so the FY's own
// start month (April) leads the trend line rather than January.
const FY_MONTH_LABELS = [
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
  "Jan",
  "Feb",
  "Mar",
];

const aggregateMonthlyTrend = (allFeesData) => {
  const buckets = new Array(12).fill(0);

  allFeesData.forEach((student) => {
    const feeDetails = student.feeDetails;
    if (!feeDetails) return;

    let studentTotal = 0;
    if (feeDetails.developmentCharges?.paid)
      studentTotal += feeDetails.developmentCharges.TotalAmt || 0;
    if (feeDetails.admissionFee?.paid)
      studentTotal += feeDetails.admissionFee.TotalAmt || 0;
    (feeDetails.monthPaid || []).forEach((m) => {
      if (m.paid) studentTotal += m.TotalAmt || 0;
    });

    const d = new Date(student.date);
    if (Number.isNaN(d.getTime())) return;
    const fyIndex = (d.getMonth() + 9) % 12; // April (month 3) -> index 0
    buckets[fyIndex] += studentTotal;
  });

  return { labels: FY_MONTH_LABELS, values: buckets };
};

// ── Main export ──────────────────────────────────────────────────────────
const FinancialYearReportPDF = ({
  feeData,
  admissionFormData = [],
  miscellaneousFeesData = [],
  duesFeeData = [],
  financialYearLabel,
  reportType = "summary",
}) => {
  if (!feeData || !Array.isArray(feeData) || feeData.length === 0) {
    alert("No data available to export.");
    return;
  }

  const fromDate = feeData[0]?.date;
  const toDate = feeData[0]?.toDate;
  const session = getSession();
  const allFeesData = feeData.flatMap((entry) => entry.monthlyFees || []);

  const { modeWiseData, modeWiseTotals, modeHeadWiseTotals, totals } =
    aggregateFeeCollection(allFeesData);
  const admissionFormAgg = aggregateSimpleFee(admissionFormData);
  const miscFeeAgg = aggregateSimpleFee(miscellaneousFeesData);
  const duesAgg = aggregateDues(duesFeeData);
  const trend = aggregateMonthlyTrend(allFeesData);

  const doc = new jsPDF({ orientation: "l", unit: "mm", format: "a4" });

  // Page 1 — cover
  drawCoverPage(doc, {
    financialYearLabel,
    reportType,
    session,
    fromDate,
    toDate,
  });
  doc.addPage();

  // Page 2 onward — dashboard
  let y = drawRunningHeader(doc, { financialYearLabel, reportType });

  // ── Collection overview (KPI cards) ──────────────────────────────────
  y = sectionHeading(doc, "Collection Overview", y);
  const kpiCards = [
    {
      label: "Admission Fee",
      value: fmt(totals.admission),
      color: COLORS.blue,
    },
    {
      label: "Development Charges",
      value: fmt(totals.development),
      color: COLORS.amber,
    },
    { label: "Monthly Fee", value: fmt(totals.monthly), color: COLORS.purple },
    {
      label: "Transport Fee",
      value: fmt(totals.transport),
      color: COLORS.blue,
    },
    ...(totals.lab
      ? [{ label: "Lab Fee", value: fmt(totals.lab), color: COLORS.amber }]
      : []),
    {
      label: "Admission Form Fee",
      value: fmt(admissionFormAgg.total),
      color: COLORS.purple,
    },
    {
      label: "Miscellaneous Fee",
      value: fmt(miscFeeAgg.total),
      color: COLORS.green,
    },
    {
      label: "Total Collection (FY)",
      value: fmt(totals.grand),
      color: COLORS.primary,
      featured: true,
    },
    {
      label: "Total Outstanding Dues",
      value: fmt(duesAgg.totalDueAllStudents),
      color: COLORS.red,
      featured: true,
    },
  ];
  y = drawKpiGrid(doc, kpiCards, y, { perRow: 4, cardH: 27 });
  y += 4;

  // ── Collection breakdown: donut (payment mode) + top fee heads ──────
  y = sectionHeading(doc, "Collection Breakdown", y);
  y = ensureSpace(doc, y, 58);
  const breakdownTop = y;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.slate);
  doc.text("PAYMENT MODE SPLIT", 14, breakdownTop);
  doc.text("TOP FEE HEADS", 160, breakdownTop);
  doc.setTextColor(0, 0, 0);

  const donutSegments = Object.entries(modeWiseTotals).map(
    ([mode, amount]) => ({
      label: mode,
      value: amount,
      color: modeColor(mode),
    }),
  );
  const donutBottom = drawDonutWithLegend(
    doc,
    14,
    breakdownTop + 6,
    42,
    donutSegments,
  );

  const feeHeadItems = [
    { label: "Monthly Fee", value: totals.monthly },
    { label: "Admission Fee", value: totals.admission },
    { label: "Development Charges", value: totals.development },
    { label: "Transport Fee", value: totals.transport },
    ...(totals.lab ? [{ label: "Lab Fee", value: totals.lab }] : []),
  ]
    .sort((a, b) => b.value - a.value)
    .filter((item) => item.value > 0);
  const listBottom = drawLeaderList(
    doc,
    160,
    breakdownTop + 10,
    122,
    feeHeadItems,
  );

  y = Math.max(donutBottom, listBottom) + 8;

  // ── Collection trend across the financial year ───────────────────────
  y = sectionHeading(doc, "Collection Trend \u2014 Apr to Mar", y);
  y = ensureSpace(doc, y, 68);
  const lineChartW = 269;
  const lineChartH = lineChartW / (900 / 320);
  try {
    const lineImg = buildLineChartImage(trend.labels, trend.values, {
      color: rgbStr(COLORS.primary),
      fill: rgbaStr(COLORS.primary, 0.12),
    });
    doc.addImage(lineImg, "PNG", 14, y, lineChartW, lineChartH);
    y += lineChartH + 8;
  } catch (e) {
    // Canvas unavailable — skip the chart, everything else still renders.
  }

  // ── Outstanding dues summary ─────────────────────────────────────────
  y = ensureSpace(doc, y, 44);
  y = sectionHeading(
    doc,
    "Outstanding Dues Summary (as on report generation date)",
    y,
  );
  const duesCards = [
    {
      label: "Students with Dues",
      value: fmt(duesAgg.studentCount),
      color: COLORS.red,
    },
    {
      label: "Monthly Fee Dues",
      value: fmt(duesAgg.totalMonthlyDue),
      color: COLORS.red,
    },
    {
      label: "Transport Fee Dues",
      value: fmt(duesAgg.totalTransportDue),
      color: COLORS.red,
    },
    {
      label: "Total Outstanding Dues",
      value: fmt(duesAgg.totalDueAllStudents),
      color: COLORS.red,
      featured: true,
    },
    {
      label: "Dues \u2014 Sibling Students",
      value: fmt(duesAgg.totalDueOfSiblingStudents),
      color: COLORS.amber,
    },
  ];
  y = drawKpiGrid(doc, duesCards, y, { perRow: 3, cardH: 27 });

  if (reportType === "summary") {
    addFooters(doc, financialYearLabel);
    doc.save(`FinancialReport_Summary_${financialYearLabel}.pdf`);
    return;
  }

  // =========================== DETAILED SECTIONS ===========================

  // ── Mode-wise detailed receipt tables ────────────────────────────────
  Object.entries(modeWiseData).forEach(([mode, rows]) => {
    doc.addPage();
    const pw = doc.internal.pageSize.getWidth();
    doc.setFont("helvetica", "bold").setFontSize(13);
    doc.setTextColor(...COLORS.navy);
    doc.text(`${mode} Collection — Detailed`, pw / 2, 18, { align: "center" });
    doc.setTextColor(0, 0, 0);
    autoTable(doc, {
      startY: 24,
      head: [
        [
          "SL No.",
          "Receipt No.",
          "Admission No.",
          "Date",
          "Student Name",
          "Class & Section",
          "Months Paid",
          "Total Amount",
          "Collected By",
        ],
      ],
      body: [
        ...rows,
        ["Total", "", "", "", "", "", "", fmt(modeWiseTotals[mode]), ""],
      ],
      theme: "grid",
      styles: { fontSize: 6.5, halign: "center", cellPadding: 2 },
      headStyles: { fillColor: COLORS.navy, textColor: [255, 255, 255] },
      didParseCell: (d) => {
        if (d.row.index === rows.length) {
          d.cell.styles.fillColor = COLORS.bgPanel;
          d.cell.styles.fontStyle = "bold";
        }
      },
    });
  });

  // ── Fee head vs payment mode summary ─────────────────────────────────
  doc.addPage();
  {
    const pw = doc.internal.pageSize.getWidth();
    doc.setFont("helvetica", "bold").setFontSize(13);
    doc.setTextColor(...COLORS.navy);
    doc.text(
      "Total Collection Summary — Fee Head vs Payment Mode",
      pw / 2,
      18,
      { align: "center" },
    );
    doc.setTextColor(0, 0, 0);
  }
  const headWiseRows = Object.entries(modeHeadWiseTotals).map(([mode, t]) => [
    mode,
    fmt(t.admission),
    fmt(t.development),
    fmt(t.monthly),
    fmt(t.transport),
    fmt(
      (t.admission || 0) +
        (t.development || 0) +
        (t.monthly || 0) +
        (t.transport || 0),
    ),
  ]);
  headWiseRows.push([
    "Grand Total",
    fmt(totals.admission),
    fmt(totals.development),
    fmt(totals.monthly),
    fmt(totals.transport),
    fmt(totals.grand),
  ]);
  autoTable(doc, {
    startY: 24,
    head: [
      [
        "Payment Mode",
        "Admission Fee",
        "Development Charges",
        "Monthly Fee",
        "Transport Fee",
        "Total Amount",
      ],
    ],
    body: headWiseRows,
    theme: "grid",
    styles: { fontSize: 7, halign: "center", cellPadding: 2.5 },
    headStyles: { fillColor: COLORS.navy, textColor: [255, 255, 255] },
    didParseCell: (d) => {
      if (d.row.index === headWiseRows.length - 1) {
        d.cell.styles.fillColor = COLORS.bgPanel;
        d.cell.styles.fontStyle = "bold";
      }
    },
  });

  // ── Admission Form Fee — detailed ────────────────────────────────────
  doc.addPage();
  {
    const pw = doc.internal.pageSize.getWidth();
    doc.setFont("helvetica", "bold").setFontSize(13);
    doc.setTextColor(...COLORS.navy);
    doc.text("Admission Form Fee Collection — Detailed", pw / 2, 18, {
      align: "center",
    });
    doc.setTextColor(0, 0, 0);
  }
  const admissionRows = (admissionFormData || []).map((item, i) => [
    i + 1,
    item.formNo || "",
    item.applyDate || "",
    item?.student?.name || "",
    item?.student?.Class || "",
    item?.student?.fatherName || "",
    item?.student?.contactNo || "",
    fmt(item.amount),
    item.mode || "N/A",
    item.collectedBy || "N/A",
  ]);
  autoTable(doc, {
    startY: 24,
    head: [
      [
        "SL No.",
        "Form No.",
        "Apply Date",
        "Student Name",
        "Class",
        "Father's Name",
        "Contact No.",
        "Amount",
        "Mode",
        "Collected By",
      ],
    ],
    body: admissionRows.length
      ? [
          ...admissionRows,
          [
            "Total",
            "",
            "",
            "",
            "",
            "",
            "",
            fmt(admissionFormAgg.total),
            "",
            "",
          ],
        ]
      : [["No Admission Form Fee Records", "", "", "", "", "", "", "", "", ""]],
    theme: "grid",
    styles: { fontSize: 6.5, halign: "center", cellPadding: 2 },
    headStyles: { fillColor: COLORS.navy, textColor: [255, 255, 255] },
    didParseCell: (d) => {
      if (admissionRows.length && d.row.index === admissionRows.length) {
        d.cell.styles.fillColor = COLORS.bgPanel;
        d.cell.styles.fontStyle = "bold";
      }
    },
  });

  // ── Miscellaneous Fee — detailed ─────────────────────────────────────
  doc.addPage();
  {
    const pw = doc.internal.pageSize.getWidth();
    doc.setFont("helvetica", "bold").setFontSize(13);
    doc.setTextColor(...COLORS.navy);
    doc.text("Miscellaneous Fee Collection — Detailed", pw / 2, 18, {
      align: "center",
    });
    doc.setTextColor(0, 0, 0);
  }
  const miscRows = (miscellaneousFeesData || []).map((item, i) => [
    i + 1,
    item.receipt_no || "",
    item.formNo || "",
    item.studentName || "",
    item.type || "",
    fmt(item.amount),
    item.mode || "N/A",
    item.collectedBy || "N/A",
  ]);
  autoTable(doc, {
    startY: 24,
    head: [
      [
        "SL No.",
        "Receipt No.",
        "Form No.",
        "Student Name",
        "Type",
        "Amount",
        "Mode",
        "Collected By",
      ],
    ],
    body: miscRows.length
      ? [...miscRows, ["Total", "", "", "", "", fmt(miscFeeAgg.total), "", ""]]
      : [["No Miscellaneous Fee Records", "", "", "", "", "", "", ""]],
    theme: "grid",
    styles: { fontSize: 6.5, halign: "center", cellPadding: 2 },
    headStyles: { fillColor: COLORS.navy, textColor: [255, 255, 255] },
    didParseCell: (d) => {
      if (miscRows.length && d.row.index === miscRows.length) {
        d.cell.styles.fillColor = COLORS.bgPanel;
        d.cell.styles.fontStyle = "bold";
      }
    },
  });

  // ── Outstanding Dues — student-wise detail ───────────────────────────
  doc.addPage();
  {
    const pw = doc.internal.pageSize.getWidth();
    doc.setFont("helvetica", "bold").setFontSize(13);
    doc.setTextColor(...COLORS.navy);
    doc.text("Outstanding Dues — Student-wise Detail", pw / 2, 18, {
      align: "center",
    });
    doc.setTextColor(0, 0, 0);
  }
  const duesRows = (duesAgg.dues || [])
    .filter((s) => (s.unpaidMonths || []).length > 0)
    .map((s, i) => {
      const unpaidMonths = s.unpaidMonths || [];
      const monthlyFee = s.monthlyFee || 0;
      const transportFee = s.transportFee || 0;
      const transportableMonths = unpaidMonths.filter((m) => m !== "June");
      const totalDue =
        monthlyFee * unpaidMonths.length +
        transportFee * transportableMonths.length;
      return [
        i + 1,
        s.studentDetails?.admission_no || "",
        s.studentDetails?.name || "",
        s.studentDetails?.currentClass || "",
        s.studentDetails?.currentSection || "",
        unpaidMonths.join(", "),
        fmt(totalDue),
      ];
    });
  autoTable(doc, {
    startY: 24,
    head: [
      [
        "SL No.",
        "Admission No.",
        "Student Name",
        "Class",
        "Section",
        "Unpaid Months",
        "Total Due",
      ],
    ],
    body: duesRows.length
      ? [
          ...duesRows,
          ["Grand Total", "", "", "", "", "", fmt(duesAgg.totalDueAllStudents)],
        ]
      : [["No Dues Records", "", "", "", "", "", ""]],
    theme: "grid",
    styles: { fontSize: 6.5, halign: "center", cellPadding: 2 },
    headStyles: { fillColor: COLORS.navy, textColor: [255, 255, 255] },
    didParseCell: (d) => {
      if (duesRows.length && d.row.index === duesRows.length) {
        d.cell.styles.fillColor = COLORS.bgPanel;
        d.cell.styles.fontStyle = "bold";
      }
    },
  });

  addFooters(doc, financialYearLabel);
  doc.save(`FinancialReport_Detailed_${financialYearLabel}.pdf`);
};

export default FinancialYearReportPDF;
